import { UtilFns } from '@/shared/util-fns';
import { Component } from '@angular/core';
import { Entity } from 'breeze-client';
import { ActiveStatusEnum, Feature, FeatureChoice, Product, ProductFeatureChoice, ProductType, ProductTypeFeatureMap } from 'app/model/entities/entity-model';
import { DialogBaseComponent, DialogParams } from 'app/shared/dialog-base.component';
import { DomainService } from 'app/shared/domain.service';
import { EntityFns } from 'app/shared/entity-fns';
import _ from 'lodash';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

export interface ProductTypeChangeFeaturesDialogParams extends DialogParams {
  productType: ProductType;
  /**
   * Whether the page is adding this style rather than editing one. The dialog cannot work this out
   * for itself: Add Product Type stamps the new entity Unchanged and clears its temp key, so every
   * Breeze state flag reports a style the server has never seen as though it were persisted.
   */
  isAdding?: boolean;
}

interface ChoiceMapping {
  fromFc: FeatureChoice;
  toFc: FeatureChoice | null;
  productCount: number;
}

@Component({
  templateUrl: './product-type-change-features-dialog.component.html',
  standalone: false,
})
export class ProductTypeChangeFeaturesDialogComponent extends DialogBaseComponent<ProductTypeChangeFeaturesDialogParams> {
  productType: ProductType;
  products: Product[];
  fromFeatures: Feature[];
  fromFeature: Feature;
  toFeatures: Feature[];
  toFeature: Feature;

  mappings: ChoiceMapping[] = [];
  toChoiceOptions: FeatureChoice[] = [];

  constructor(
    public config: DynamicDialogConfig<ProductTypeChangeFeaturesDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
  ) {
    super(config, dialogRef, domainService);

    Object.assign(this.config, {
      header: 'Product Type Feature Change',
      width: '1200px',
      height: '900px',
    });
    this.prepare();
  }

  async prepare() {
    this.productType = this.params.productType;
    this.products = this.productType.products.slice();
    this.fromFeatures = _.sortBy(this.productType.productTypeFeatureMaps, (x) => x.displayOrder).map((x) => x.feature);
    const toFeatures = await this.dbQueryService.getFeatures(true);
    this.toFeatures = toFeatures.filter((x) => !this.fromFeatures.some((y) => y.id === x.id));
    this.fromFeature = this.fromFeatures[0];

    this.recomputeMappings();

    this.isPageReady = true;
  }

  onFromFeatureChange() {
    this.recomputeMappings();
  }

  onToFeatureChange() {
    this.recomputeMappings();
  }

  recomputeMappings() {
    if (this.fromFeature == null) {
      this.mappings = [];
      this.toChoiceOptions = [];
      return;
    }

    const sourceChoices: FeatureChoice[] = _.uniq(
      _.flatten(
        this.products.map((p) =>
          p.productFeatureChoices.filter((pfc) => pfc.featureChoice.featureId === this.fromFeature.id).map((pfc) => pfc.featureChoice),
        ),
      ),
    );

    const usageCounts = new Map<number, number>();
    this.products.forEach((p) => {
      p.productFeatureChoices
        .filter((pfc) => pfc.featureChoice.featureId === this.fromFeature.id)
        .forEach((pfc) => {
          usageCounts.set(pfc.featureChoiceId, (usageCounts.get(pfc.featureChoiceId) ?? 0) + 1);
        });
    });

    const sorted = _.sortBy(sourceChoices, (x) => x.displayOrder);

    this.toChoiceOptions = this.toFeature == null
      ? []
      : _.sortBy(
          this.toFeature.featureChoices.filter((x) => x.activeStatusId === ActiveStatusEnum.Active),
          (x) => x.displayOrder,
        );

    this.mappings = sorted.map((fromFc) => ({
      fromFc,
      toFc: this.toChoiceOptions.find((y) => y.choiceValue == fromFc.choiceValue) ?? null,
      productCount: usageCounts.get(fromFc.id) ?? 0,
    }));
  }

  isMappingComplete(): boolean {
    if (this.toFeature == null) return false;

    // No length test. A style with no products yet has no choices to remap, and the change is only
    // which feature is attached - which is exactly the state a style is in while its size scale is
    // still being settled, so refusing there blocks the one moment the swap is free.
    return this.mappings.every((m) => m.toFc != null);
  }

  async ok() {
    if (!this.isMappingComplete()) return;

    const clusters = this.computeDuplicateClusters();

    let confirmMsg = this.mappings.length === 0
      ? `Do you want to change the feature '${this.fromFeature.name}' to '${this.toFeature.name}'`
        + `<br/>on '${this.productType.name}'? This style has no products, so nothing moves.`
      : `Are you sure you want to change the feature '${this.fromFeature.name}' to '${this.toFeature.name}'<br/>for all products of type '${this.productType.name}'?`;
    if (clusters.length > 0) {
      const totalCollapsed = _.sumBy(clusters, (c) => c.fromProductIds.length);
      const labels = clusters.map((c) => c.label).join(", ");
      confirmMsg += `<br/><br/>This will also combine ${totalCollapsed} product(s) that map to the same target choice (${labels}).`;
    }

    const isOk = await this.dialogService.yesNoDialog(`Confirm feature change`, confirmMsg, 'Ok', 'Cancel');
    if (!isOk) return;

    const touched = this.applyMappings();

    // A style the server has never issued an id for cannot have its maps written on their own - the
    // insert fails on FK_ProductTypeFeatureMap_ProductType.
    //
    // Confirmed positively, and deliberately not from Breeze's state flags. Add Product Type calls
    // setUnchanged() on the new entity (createNewProductTypeAsUnchanged), which reports it as
    // Unchanged, then Modified once anything is typed, and clears its temp key - so isAdded(),
    // isUnchanged() and hasTempKey all describe a brand new style as if it were saved. The id and
    // the page's own flag are the only two things that do not lie here, so both must agree before
    // anything is written.
    const isPersistedStyle = !this.params.isAdding && this.productType.id > 0;
    if (!isPersistedStyle) {
      this.toastr.success('The system changed the feature. Save the page to keep the change.');
      this.dialogRef.close(null);
      return;
    }

    // Only what this dialog changed. Saving everything pending also commits whatever the operator
    // has typed into the page behind the dialog - they opened this to swap a feature, not to save
    // a form they are still filling in, and the server judges that form as though it were finished.
    await this.dbSaveService.saveOwnChanges(touched);

    // Reached only when the save went through (a refusal throws). Each cluster is an exact
    // duplicate after the swap, which is the one kind of combine the server accepts.
    for (const c of clusters) {
      await this.dbSaveService.combineProducts(c.fromProductIds, c.toProductId);
    }

    this.toastr.success(`Feature change completed`);
    this.dialogRef.close(null);
  }

  cancel() {
    this.dialogRef.close(null);
  }

  private computeDuplicateClusters(): { toProductId: number; fromProductIds: number[]; label: string }[] {
    const fromToById = new Map<number, FeatureChoice>();
    this.mappings.forEach((m) => fromToById.set(m.fromFc.id, m.toFc!));

    // Build each Product's resulting full FeatureChoice-set after the mapping is applied.
    // Key = sorted choiceIds joined; two Products with the same key collapse to one.
    const byKey = new Map<string, Product[]>();
    this.products.forEach((prod) => {
      const resultingIds = prod.productFeatureChoices.map((pfc) => {
        if (pfc.featureChoice.featureId === this.fromFeature.id) {
          return fromToById.get(pfc.featureChoiceId)!.id;
        }
        return pfc.featureChoiceId;
      });
      const key = _.sortBy(resultingIds).join(",");
      const bucket = byKey.get(key) ?? [];
      bucket.push(prod);
      byKey.set(key, bucket);
    });

    const clusters: { toProductId: number; fromProductIds: number[]; label: string }[] = [];
    byKey.forEach((bucket) => {
      if (bucket.length < 2) return;
      const sorted = _.sortBy(bucket, (p) => p.id);
      const to = sorted[0];
      const fromIds = sorted.slice(1).map((p) => p.id);
      const targetFc = sorted[0].productFeatureChoices
        .map((pfc) => (pfc.featureChoice.featureId === this.fromFeature.id ? fromToById.get(pfc.featureChoiceId)! : null))
        .find((fc) => fc != null);
      clusters.push({
        toProductId: to.id,
        fromProductIds: fromIds,
        label: targetFc?.choiceValue ?? "",
      });
    });
    return clusters;
  }

  /** Returns the entities this dialog changed, so the save can be limited to them. */
  private applyMappings(): Entity[] {
    const touched: Entity[] = [];

    const fromToById = new Map<number, FeatureChoice>();
    this.mappings.forEach((m) => {
      UtilFns.assert(m.toFc != null, `No target choice picked for '${m.fromFc.choiceValue}'`);
      fromToById.set(m.fromFc.id, m.toFc!);
    });

    this.products.forEach((prod) => {
      const pfcs = prod.productFeatureChoices.filter((x) => x.featureChoice.featureId == this.fromFeature.id);
      pfcs.forEach((pfc) => {
        const toFc = fromToById.get(pfc.featureChoiceId);
        UtilFns.assert(toFc != null, `No matching feature choice found for ${pfc.featureChoice.choiceValue}`);
        // because we are changing the key its safer to delete the old and add the new.
        pfc.entityAspect.setDeleted();
        touched.push(pfc);
        touched.push(
          this.dbSaveService.createEntity(ProductFeatureChoice, {
            productId: prod.id,
            featureChoiceId: toFc!.id,
          }),
        );
      });
    });

    const ptfm = this.productType.productTypeFeatureMaps.find((x) => x.featureId == this.fromFeature.id);
    UtilFns.assert(ptfm != null, `No ProductTypeFeatureMap found for feature ${this.fromFeature.id}`);
    // On an unsaved style this map was never persisted, so it has to be detached rather than
    // marked deleted - a Deleted row the server has never seen is a row it cannot delete. A
    // detached row must also stay out of the save list, which is what the state check is for.
    const stateAfter = EntityFns.deleteOrDetach(ptfm!.entityAspect);
    if (!stateAfter.isDetached()) touched.push(ptfm!);

    touched.push(
      this.dbSaveService.createEntity(ProductTypeFeatureMap, {
        productTypeId: ptfm!.productTypeId,
        featureId: this.toFeature.id,
        // The replacement stands where the original stood. Without this the swapped feature drops
        // to the front of the list and the Product Builder asks for sizes before colors.
        displayOrder: ptfm!.displayOrder,
      }),
    );

    return touched;
  }
}
